import { useState, useEffect, useRef } from "react";
import { useAudioPlayer, Track } from "../useAudioPlayer";
import { SNIPPIT_API_URL, SNIPPIT_GAME_URL } from "../App";
import {
  evaluateChartGuess,
  evaluateArtistGuess,
  GuessResult,
} from "../utils/guessHandlers";
import { Footer } from "./LandingView";
import { setClipboard } from "./PlayerView";

type GameState = {
  tracks: GameTrack[];
  date: Date;
  dateStarted: Date;
  currentIndex: number;
  score: number;
  guesses: { [key: number]: GuessResult };
};

type SavedGameState = {
  dateStarted: Date;
  currentIndex: number;
  score: number;
  guesses: { [key: number]: GuessResult };
};

type GameTrack = Track & {
  artists: [];
  lastWeek: number;
  peakPosition: number;
  weeksOnChart: number;
};

type CurrentGuessState = {
  titleGuess: string | null;
  titleCorrect: boolean | null;
  artistGuesses: string[];
  artistsCorrect: string[];
  artistsRemaining: string[];
  allArtistsGuessed: boolean;
};

const STORAGE_KEY = "chartGameState";
let isInitialLoad = true;

export function ChartGameView() {
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [loading, setLoading] = useState(true);
  const [titleInput, setTitleInput] = useState("");
  const [artistInput, setArtistInput] = useState("");
  const [currentGuessState, setCurrentGuessState] = useState<CurrentGuessState>(
    {
      titleGuess: null,
      artistGuesses: [],
      titleCorrect: null,
      artistsCorrect: [],
      artistsRemaining: [],
      allArtistsGuessed: false,
    },
  );
  const [showInstructions, setShowInstructions] = useState(false);
  const activeTrackRef = useRef<HTMLDivElement>(null);
  const emptyGuessState = (track?: GameTrack): CurrentGuessState => ({
    titleGuess: null,
    titleCorrect: null,
    artistGuesses: [],
    artistsCorrect: [],
    artistsRemaining: track?.artists ?? [],
    allArtistsGuessed: false,
  });

  useEffect(() => {
    const fetchGameTracks = async () => {
      try {
        // Helper function to get today's date in Eastern Time
        const getEasternDate = () => {
          const now = new Date();
          const easternTime = new Date(
            now.toLocaleString("en-US", { timeZone: "America/New_York" }),
          );
          return new Date(
            easternTime.getFullYear(),
            easternTime.getMonth(),
            easternTime.getDate(),
          );
        };

        // Check if we have a saved game state for today (Eastern Time)
        const savedState = localStorage.getItem(STORAGE_KEY);
        if (savedState) {
          try {
            const parsed = JSON.parse(savedState) as SavedGameState;
            const savedDate = new Date(parsed.dateStarted);
            const todayEastern = getEasternDate();

            const isLegacyShape = Object.values(parsed.guesses ?? {}).some(
              (g: any) => "artist" in g && !("artistGuesses" in g),
            );

            if (
              isLegacyShape ||
              savedDate.toDateString() !== todayEastern.toDateString()
            ) {
              localStorage.removeItem(STORAGE_KEY);
            }

            else if (savedDate.toDateString() === todayEastern.toDateString()) {
              // Fetch fresh tracks but restore progress
              const response = await fetch(
                `${SNIPPIT_API_URL}/api/charts/daily/hot100/game`,
              );
              const data: { tracks: GameTrack[]; gameDate: string } =
                await response.json();

              const restoredState: GameState = {
                tracks: data.tracks,
                date: new Date(data.gameDate),
                dateStarted: new Date(parsed.dateStarted),
                currentIndex: parsed.currentIndex,
                score: parsed.score,
                guesses: parsed.guesses,
              };
              setGameState(restoredState);
              isInitialLoad = false;
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error("Error parsing saved state:", error);
            localStorage.removeItem(STORAGE_KEY);
          }
        }

        // Otherwise fetch fresh game
        const response = await fetch(
          `${SNIPPIT_API_URL}/api/charts/daily/hot100/game`,
        );
        const data: { tracks: GameTrack[]; gameDate: string } =
          await response.json();

        const newGameState: GameState = {
          tracks: data.tracks,
          date: new Date(data.gameDate),
          dateStarted: new Date(),
          currentIndex: 0,
          score: 0,
          guesses: {},
        };

        setGameState(newGameState);
        isInitialLoad = false;
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch game tracks:", error);
        setLoading(false);
      }
    };

    fetchGameTracks();
  }, []);

  // Save only necessary game state to localStorage (not tracks)
  useEffect(() => {
    if (gameState && !isInitialLoad) {
      const savedState: SavedGameState = {
        dateStarted: gameState.dateStarted,
        currentIndex: gameState.currentIndex,
        score: gameState.score,
        guesses: gameState.guesses,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(savedState));
    }
  }, [gameState]);

  // Add this effect to restore currentGuessState when currentIndex changes
  useEffect(() => {
    if (!gameState) return;
    const guess = gameState.guesses[gameState.currentIndex];
    const track = gameState.tracks[gameState.currentIndex];
    if (guess) {
      setCurrentGuessState({
        titleGuess: guess.title || null,
        titleCorrect: guess.titleCorrect,
        artistGuesses: guess.artistGuesses,
        artistsCorrect: guess.artistsCorrect,
        artistsRemaining: guess.artistsRemaining,
        allArtistsGuessed: guess.artistsRemaining.length === 0,
      });
    } else {
      setCurrentGuessState(emptyGuessState(track));
    }
  }, [gameState?.currentIndex, gameState]);

  // Add this effect to scroll when currentIndex changes
  useEffect(() => {
    if (activeTrackRef.current) {
      activeTrackRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [gameState?.currentIndex]);

  const { isPlaying, progress, toggle, setCurrentIndex } = useAudioPlayer(
    gameState?.tracks ?? [],
    false,
    false,
  );

  const handleSubmitTitle = () => {
    if (!gameState || !titleInput.trim()) return;

    const currentTrack = gameState.tracks[gameState.currentIndex];
    const { correct, pointsGained } = evaluateChartGuess(
      titleInput,
      currentTrack.title,
      "title",
    );

    setCurrentGuessState((prev) => ({
      ...prev,
      titleGuess: titleInput,
      titleCorrect: correct,
    }));

    setGameState((prev) => {
      if (!prev) return prev;
      const existing = prev.guesses[prev.currentIndex];
      return {
        ...prev,
        score: prev.score + pointsGained,
        guesses: {
          ...prev.guesses,
          [prev.currentIndex]: {
            title: titleInput,
            titleCorrect: correct,
            artistGuesses: existing?.artistGuesses ?? [],
            artistsCorrect: existing?.artistsCorrect ?? [],
            artistsRemaining:
              existing?.artistsRemaining ?? currentTrack.artists,
            pointsGained: (existing?.pointsGained ?? 0) + pointsGained,
          },
        },
      };
    });

    setTitleInput("");
  };

  const handleSubmitArtist = () => {
    if (!gameState || !artistInput.trim()) return;
    if (currentGuessState.allArtistsGuessed) return;

    const currentTrack = gameState.tracks[gameState.currentIndex];
    const { matchedArtist, pointsGained } = evaluateArtistGuess(
      artistInput,
      currentTrack.artists,
      currentGuessState.artistsCorrect,
    );

    const newCorrect = matchedArtist
      ? [...currentGuessState.artistsCorrect, matchedArtist]
      : currentGuessState.artistsCorrect;
    const newRemaining = currentTrack.artists.filter(
      (a) => !newCorrect.includes(a),
    );
    const allDone = newRemaining.length === 0;

    setCurrentGuessState((prev) => ({
      ...prev,
      artistGuesses: [...prev.artistGuesses, artistInput],
      artistsCorrect: newCorrect,
      artistsRemaining: newRemaining,
      allArtistsGuessed: allDone,
    }));

    setGameState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        score: prev.score + pointsGained,
        guesses: {
          ...prev.guesses,
          [prev.currentIndex]: {
            ...prev.guesses[prev.currentIndex],
            title: prev.guesses[prev.currentIndex]?.title ?? "",
            titleCorrect:
              prev.guesses[prev.currentIndex]?.titleCorrect ?? false,
            artistGuesses: [
              ...(prev.guesses[prev.currentIndex]?.artistGuesses ?? []),
              artistInput,
            ],
            artistsCorrect: newCorrect,
            artistsRemaining: newRemaining,
            pointsGained:
              (prev.guesses[prev.currentIndex]?.pointsGained ?? 0) +
              pointsGained,
          },
        },
      };
    });

    setArtistInput("");
  };

  const handleSkipTitleGuess = () => {
    if (!gameState) return;
    const currentTrack = gameState.tracks[gameState.currentIndex];

    setCurrentGuessState((prev) => ({
      ...prev,
      titleGuess: "SKIPPED",
      titleCorrect: false,
    }));

    setGameState((prev) => {
      if (!prev) return prev;
      const existing = prev.guesses[prev.currentIndex];
      return {
        ...prev,
        guesses: {
          ...prev.guesses,
          [prev.currentIndex]: {
            title: "SKIPPED",
            titleCorrect: false,
            artistGuesses: existing?.artistGuesses ?? [],
            artistsCorrect: existing?.artistsCorrect ?? [],
            artistsRemaining:
              existing?.artistsRemaining ?? currentTrack.artists,
            pointsGained: existing?.pointsGained ?? 0,
          },
        },
      };
    });
  };

  const handleSkipArtistGuess = () => {
    if (!gameState) return;

    setCurrentGuessState((prev) => ({
      ...prev,
      artistGuesses: [...prev.artistGuesses, "SKIPPED"],
      artistsRemaining: [],
      allArtistsGuessed: true,
    }));

    setGameState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        guesses: {
          ...prev.guesses,
          [prev.currentIndex]: {
            ...prev.guesses[prev.currentIndex],
            title: prev.guesses[prev.currentIndex]?.title ?? "",
            titleCorrect:
              prev.guesses[prev.currentIndex]?.titleCorrect ?? false,
            artistGuesses: [
              ...(prev.guesses[prev.currentIndex]?.artistGuesses ?? []),
              "SKIPPED",
            ],
            artistsCorrect:
              prev.guesses[prev.currentIndex]?.artistsCorrect ?? [],
            artistsRemaining: [],
            pointsGained: prev.guesses[prev.currentIndex]?.pointsGained ?? 0,
          },
        },
      };
    });
  };

  const handleNextTrack = () => {
    if (!gameState || gameState.currentIndex >= gameState.tracks.length - 1)
      return;
    const nextIndex = gameState.currentIndex + 1;
    setCurrentIndex(nextIndex);
    setGameState((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        currentIndex: nextIndex,
        guesses: {
          ...prev.guesses,
          [prev.currentIndex]: {
            title: currentGuessState.titleGuess ?? "",
            titleCorrect: currentGuessState.titleCorrect ?? false,
            artistGuesses: currentGuessState.artistGuesses,
            artistsCorrect: currentGuessState.artistsCorrect,
            artistsRemaining: currentGuessState.artistsRemaining ?? [],
            pointsGained: prev.guesses[prev.currentIndex]?.pointsGained ?? 0,
          },
        },
      };
    });
    setCurrentGuessState(emptyGuessState(gameState.tracks[nextIndex]));
  };

  const handleBack = () => {
    window.location.href = "/";
  };

  if (loading)
    return (
      <div className="game-view">
        <div className="grain" />
        <div className="vignette" />
        Loading game...
        <progress />
      </div>
    );

  if (!gameState) return <div className="game-view">Failed to load game</div>;

  const currentTrack = gameState.tracks[gameState.currentIndex];
  const bothGuessed =
    currentGuessState.titleGuess !== null &&
    currentGuessState.allArtistsGuessed;

  return (
    <div className="game-view">
      <div className="grain" />
      <div className="vignette" />

      <div className="game-top-controls">
        <button onClick={handleBack} className="back-btn">
          ← Back
        </button>
        <button
          onClick={() => setShowInstructions(true)}
          className="instructions-btn"
        >
          ?
        </button>
      </div>

      {showInstructions && (
        <div
          className="instructions-modal-overlay"
          onClick={() => setShowInstructions(false)}
        >
          <div
            className="instructions-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowInstructions(false)}
            >
              ✕
            </button>
            <div className="instructions-content">
              <p>Welcome to the Billboard Hot 100 daily game!</p>
              <p>
                Every day, you get a random week in Billboard Hot 100 history,
                and you have to name the top ten singles of that week given an
                audio preview.
              </p>
              <p>
                You get{" "}
                <i>
                  <b>5 points</b>
                </i>{" "}
                for naming the correct song title.
              </p>
              <p>
                You get{" "}
                <i>
                  <b>3 points</b>
                </i>{" "}
                for correctly naming the artist.
              </p>
              <p>How well can you do?</p>
              <br />
              <p>
                Noticing any issues or have feedback?{" "}
                <a
                  className="link"
                  href="mailto:bannigeri17@gmail.com?subject=SOUND%20BITS%20FEEDBACK"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shoot me an email!
                </a>{" "}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="game-header">
        <h1>Daily Chart Challenge</h1>
        <h2 className="game-date">
          {new Date(gameState.date).toDateString() ?? ""}
        </h2>
        <div className="game-score">Score: {gameState.score}</div>
        <div className="game-progress">
          Track {gameState.currentIndex + 1} / {gameState.tracks.length}
        </div>
      </div>

      <div className="game-player">
        <button onClick={toggle} className="play-button">
          {isPlaying ? "⏸ Pause" : "▶ Play"}
        </button>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      <div className="game-chart-list">
        {gameState.tracks.map((track, idx) => {
          const currentGuess =
            idx === gameState.currentIndex ? currentGuessState : null;

          const showTitle =
            gameState.currentIndex > idx ||
            (idx === gameState.currentIndex &&
              currentGuess?.titleGuess !== null);
          const showArtist =
            gameState.currentIndex > idx ||
            (idx === gameState.currentIndex &&
              currentGuessState.allArtistsGuessed);

          const lastWeekDiffString = `${track.lastWeek > idx + 1 ? "+" : ""}${track.lastWeek !== idx + 1 ? track.lastWeek - (idx + 1) : "—"}`;
          var diffClassName = "";
          if (track.lastWeek > idx + 1) {
            diffClassName = "diff-positive";
          } else if (track.lastWeek < idx + 1) {
            diffClassName = "diff-negative";
          }

          return (
            <div
              key={track.id}
              ref={idx === gameState.currentIndex ? activeTrackRef : null}
              className={`chart-entry ${idx === gameState.currentIndex ? "active" : ""}`}
            >
              <div className="chart-rank">#{idx + 1}</div>
              <img
                className="chart-artwork-thumb"
                src={showArtist && showTitle ? track.artworkPath : undefined}
                alt="???"
              ></img>
              <div className="chart-info">
                <div className="chart-title">
                  {showTitle ? track.title : "???"}
                </div>
                <div className="chart-artist">
                  {showArtist ? (
                    <div className="artist-chips">
                      {track.artists.map((a, i) => {
                        let artistStr: string = a;

                        if (i < track.artists.length - 2) {
                          artistStr = artistStr + ", ";
                        } else if (i === track.artists.length - 2) {
                          artistStr = artistStr + " and ";
                        }
                        return (
                          <span key={i} className="artist-chip">
                            {artistStr}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    "???"
                  )}
                </div>
              </div>
              <div className="chart-info">
                <div className="chart-detail-title">Last Week</div>
                <div className="chart-detail-figure">
                  {`${track.lastWeek}`}{" "}
                  <span className={diffClassName}>({lastWeekDiffString})</span>
                </div>
              </div>
              <div className="chart-info">
                <div className="chart-detail-title">Peak Position</div>
                <div className="chart-detail-figure">{track.peakPosition}</div>
              </div>
              <div className="chart-info">
                <div className="chart-detail-title">Weeks on Chart</div>
                <div className="chart-detail-figure">{track.weeksOnChart}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="game-input-row">
        {currentGuessState.titleGuess === null ? (
          <div className="game-input-section">
            <div>
              ???
            </div>
            <input
              type="text"
              placeholder="Song title..."
              value={titleInput}
              onChange={(e) => setTitleInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmitTitle()}
              autoFocus
            />
            <div className="game-input-row">
              <button onClick={handleSubmitTitle} className="submit-button">
                Submit Title
              </button>

              <button onClick={handleSkipTitleGuess} className="skip-button">
                Skip
              </button>
            </div>
          </div>
        ) : (
          <div className="game-result">
            <p
              className={
                currentGuessState.titleCorrect ? "correct" : "incorrect"
              }
            >
              {currentGuessState.titleGuess}
              {currentGuessState.titleCorrect
                ? " ✓"
                : ` ✗ (Correct: ${currentTrack.title})`}
            </p>
          </div>
        )}

        {currentGuessState.allArtistsGuessed ? (
          <div className="game-result">
            <div className="artist-chips">
              {currentTrack.artists.map((a, i) => {
                const guessed = currentGuessState.artistsCorrect.includes(a);
                let artistString: string = a ?? "";
                if (i < currentTrack.artists.length - 2) {
                  artistString = artistString + ", ";
                } else if (i === currentTrack.artists.length - 2) {
                  artistString = artistString + " and ";
                }
                return (
                  <span
                    key={i}
                    className={`artist-chip ${guessed ? "correct" : "incorrect"}`}
                  >
                    {artistString}
                  </span>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="game-input-section">
            <div className="artist-chips">
              {currentTrack.artists.map((a, i) => {
                const guessed = currentGuessState.artistsCorrect.includes(a);
                let artistString: string = guessed ? a : "???";
                if (i < currentTrack.artists.length - 2) {
                  artistString = artistString + ", ";
                } else if (i === currentTrack.artists.length - 2) {
                  artistString = artistString + " and ";
                }

                return (
                  <span
                    key={i}
                    className={`artist-chip ${guessed ? "correct" : "pending"}`}
                  >
                    {artistString}
                  </span>
                );
              })}
            </div>
            <input
              type="text"
              placeholder={`Artist name… (${currentGuessState?.artistsRemaining.length ?? 0} remaining)`}
              value={artistInput}
              onChange={(e) => setArtistInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSubmitArtist()}
            />
            <div className="game-input-row">
              <button onClick={handleSubmitArtist} className="submit-button">
                Submit Artist
              </button>
              <button onClick={handleSkipArtistGuess} className="skip-button">
                Skip
              </button>
            </div>
          </div>
        )}
      </div>

      {bothGuessed && gameState.currentIndex < gameState.tracks.length - 1 && (
        <div className="game-button-section">
          <button
            onClick={handleNextTrack}
            disabled={gameState.currentIndex >= gameState.tracks.length - 1}
          >
            Next Track →
          </button>
        </div>
      )}

      {gameState.currentIndex === gameState.tracks.length - 1 &&
        bothGuessed && (
          <div className="game-over">
            <h2>Game Over!</h2>
            <p>Final Score: {gameState.score}</p>
            <button
              className="play-btn"
              onClick={async () => {
                let clipText: string = `I scored ${gameState.score} on today's chart game!\n${gameState.date?.toDateString()}\n${SNIPPIT_GAME_URL}/chart-game`;
                setClipboard(clipText);
              }}
            >
              SHARE
            </button>
          </div>
        )}

      {Footer()}
    </div>
  );
}
